import { Injectable } from '@angular/core';
import { HttpCacheManager } from '@ngneat/cashew';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OrgSpaceActiveAggregationData } from '@y42/org-space/models';
import { OrgSpaceQuery } from '@y42/org-space/state';
import { takePromise } from '@y42/util/take-promise';
import {
  BehaviorSubject,
  Subject,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  switchMap,
  tap,
} from 'rxjs';
import { JobDateRange, JobHttpModel } from '../../../state';
import { JobHttpServiceInterface } from './base-job-http.service';

export interface AllJobsRequestOptions {
  dateRange?: JobDateRange;
  exactLocation?: boolean;
  limit?: number;
  force?: boolean;
}

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class JobHttpService<HttpModel extends JobHttpModel = JobHttpModel>
  implements JobHttpServiceInterface<HttpModel>
{
  constructor(
    private readonly jobHttpService: JobHttpServiceInterface<HttpModel>,
    private readonly httpCacheManager: HttpCacheManager,
    private readonly orgSpaceQuery: OrgSpaceQuery,
  ) {
    this.orgSpaceQuery.aggregatedData$
      .pipe(
        map((aggregatedData) => this.getCacheKey(aggregatedData)),
        distinctUntilChanged(),
        tap(() => {
          this.httpCacheManager.clear();
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  readonly getLatestJobs = this.getJobsRequestAggregator((paths: string[]): Promise<HttpModel[]> => {
    return paths && paths.length > 0 ? this.jobHttpService.getLatestJobs(paths) : Promise.resolve([]);
  });

  readonly getLatestValidJobs = this.getJobsRequestAggregator((paths: string[]): Promise<HttpModel[]> => {
    return paths && paths.length > 0 ? this.jobHttpService.getLatestValidJobs(paths) : Promise.resolve([]);
  });

  readonly url = this.jobHttpService.url;

  async getAllJobs(
    featureOrFeatureChildPaths: string[],
    options?: AllJobsRequestOptions,
  ): Promise<{ jobs: HttpModel[]; cursor?: string; fromCache: boolean }> {
    if (featureOrFeatureChildPaths.length === 0) {
      return { jobs: [], fromCache: false };
    }

    return this.jobHttpService.getAllJobs(this.addWildcardsIfNeeded(featureOrFeatureChildPaths, options), {
      ...(options ?? {}),
      cacheKey: options?.force ? undefined : this.getCacheKey(this.orgSpaceQuery.aggregatedData()),
    });
  }

  getJob(requestId: string, path: string) {
    return this.jobHttpService.getJob(requestId, path);
  }

  private addWildcardsIfNeeded(featureOrFeatureChildPaths: string[], options?: { exactLocation?: boolean }) {
    return options?.exactLocation
      ? featureOrFeatureChildPaths
      : featureOrFeatureChildPaths.map((path) => (path.endsWith('/*') ? path : `${path}/*`));
  }

  private removeWildcardsIfNeeded(featureOrFeatureChildPaths: string[], options?: { exactLocation?: boolean }) {
    return options?.exactLocation
      ? featureOrFeatureChildPaths
      : featureOrFeatureChildPaths.map((path) => (path.endsWith('/*') ? path.slice(0, path.length - 2) : path));
  }

  private getJobsRequestAggregator(requestCallback: (paths: string[]) => Promise<HttpModel[]>) {
    const DEBOUNCE_TIME_MS = 300;
    const get$$ = new BehaviorSubject<{ paths: string[]; response$$?: Subject<HttpModel[]> }>({ paths: [] });

    get$$
      .pipe(
        filter((value) => value.paths.length > 0),
        debounceTime(DEBOUNCE_TIME_MS),
        switchMap(async (value) => {
          try {
            const paths = [...new Set<string>(value.paths)];
            const response = await requestCallback(paths);
            value.response$$?.next(response);
            value.response$$?.complete();
          } catch (error) {
            value.response$$?.error(error);
          }
        }),
        untilDestroyed(this),
      )
      .subscribe();

    return async (featureOrFeatureChildPaths: string[], options?: { exactLocation?: boolean }) => {
      if (featureOrFeatureChildPaths.length === 0) {
        return [];
      }

      const paths = this.addWildcardsIfNeeded(featureOrFeatureChildPaths, options);
      const pathsWithoutWildcard = this.removeWildcardsIfNeeded(featureOrFeatureChildPaths, options);
      const response$$ = get$$.value.response$$ ?? new Subject<HttpModel[]>();

      get$$.next({
        paths: [...get$$.value.paths, ...paths],
        response$$,
      });

      return takePromise(
        response$$.pipe(
          map((response) => {
            return response.filter((job) => {
              return pathsWithoutWildcard.some((path) => job.path.startsWith(path));
            });
          }),
          finalize(() => {
            get$$.next({ paths: [] });
          }),
        ),
      );
    };
  }

  private getCacheKey({ spaceId, orgId, branch, userId }: OrgSpaceActiveAggregationData) {
    return `${spaceId}|${orgId}|${branch}|${userId}`;
  }
}
